import { useEffect, useState } from 'react'

import { useInfiniteQuery } from '@tanstack/react-query'
import spacetime from 'spacetime'

import { getAccountActivities } from 'api/AccountActivityAPI'
import { PagedResponse } from 'api/AccountActivityAPI/types'
import { DateRange } from 'components/DateRangePicker'
import { Activity, ActivityType } from 'modules/accountActivity/domain'
import {
  emailBundle,
  getEmailDataFromActivity,
  meetingsBundle,
  pageViewBundle
} from 'utils/activityTimeline'

export const useAccountActivity = (
  useTwoWeekPreview: boolean,
  dateRange?: DateRange,
  accountId?: string,
  contactId?: string
) => {
  const [activityData, setActivityData] = useState<Record<number, Activity[]>>({})
  const [dataTotal, setDataTotal] = useState(0)

  const startTime = useTwoWeekPreview
    ? spacetime()
        .startOf('day')
        .subtract(2, 'week')
        .epoch.toString()
    : dateRange
    ? dateRange.startDate.valueOf()
    : undefined
  const endTime = useTwoWeekPreview
    ? undefined
    : dateRange
    ? dateRange.endDate.valueOf()
    : undefined
  const { fetchNextPage, hasNextPage, data, isLoading, error } = useInfiniteQuery({
    queryKey: [`getAccountActivities-${startTime ? startTime : 'full'}`, accountId ?? contactId],
    queryFn: ({ pageParam = 1 }) =>
      getAccountActivities(pageParam, 25, accountId, contactId, startTime, endTime),
    getNextPageParam: (lastPage: PagedResponse<Activity>) =>
      lastPage.data.length > 0 ? lastPage.nextPageCursor : undefined
  })

  useEffect(() => {
    const combinedPages = data?.pages.flatMap(page => page.data) ?? []
    let bundledEvents = []
    bundledEvents = combinedPages.reduce(
      (acc: Activity[], activity: Activity, index: number, startArray: Activity[]) => {
        switch (activity.activityType) {
          case ActivityType.PAGE_VIEW:
            acc = pageViewBundle(activity, startArray[index - 1], acc)
            break
          case ActivityType.MEETING_BOOKED:
            acc = meetingsBundle(activity, startArray[index - 1], acc)
            break
          case ActivityType.EMAIL_CLICK:
          case ActivityType.EMAIL_CLICK_SFDC:
          case ActivityType.EMAIL_OPEN:
          case ActivityType.EMAIL_OPEN_SFDC:
            acc = emailBundle(activity, acc)
            break
          case ActivityType.EMAIL_INBOUND:
            acc.push({
              ...activity,
              activityContext: {
                ...activity.activityContext,
                emailPreviewData: getEmailDataFromActivity(activity)
              }
            })
            break
          default:
            acc.push(activity)
            break
        }
        return acc
      },
      []
    )

    //group activities by day
    const groupedData = bundledEvents.reduce(
      (acc: Record<number, Activity[]>, activity: Activity) => {
        const date = spacetime(activity.createdAt).startOf('day').epoch
        if (!acc[date]) {
          acc[date] = []
        }
        acc[date].push(activity)
        return acc
      },
      {}
    )
    setActivityData(groupedData)
    setDataTotal(bundledEvents?.length ?? 0)
  }, [data])

  return {
    data: activityData,
    dataTotal,
    error,
    isLoading,
    hasMoreActivity: hasNextPage,
    fetchNextPage
  }
}
